import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue } from 'jotai';
import { Controller,useForm } from 'react-hook-form';

import { createOrderNoteFormSchema } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Stack, Text,TextArea, toast } from '@ekhodealer/ekho-common/rev';

import { useCreateOrderNote } from '../../api/useCreateOrderNote';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';

const MAX_NOTE_LENGTH = 4000;

interface FormData {
	noteContent: string;
	sendToCommChannels: boolean;
	sendToBuyer: boolean;
}

/**
 * NoteForm component
 * Form for creating new order notes with validation
 */
export const NoteForm = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;

	const createNoteMutation = useCreateOrderNote(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		watch,
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(createOrderNoteFormSchema),
		defaultValues: {
			noteContent: '',
			sendToCommChannels: false,
			sendToBuyer: false,
		},
	});

	const noteContent = watch('noteContent');
	const characterCount = noteContent?.length || 0;
	const isOverLimit = characterCount > MAX_NOTE_LENGTH;

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await createNoteMutation.mutateAsync({
				noteContent: data.noteContent.trim(),
				sendToCommChannels: data.sendToCommChannels,
				sendToBuyer: data.sendToBuyer,
			});

			toast({
				message: 'Note created successfully',
				variant: 'success',
			});

			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to create note';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='16'>
			<Stack itemsSpacing='8'>
				<Controller
					name='noteContent'
					control={control}
					render={({ field: { onChange, onBlur, value } }) => (
						<Stack itemsSpacing='4'>
							<TextArea
								placeholder='Enter your order note here'
								value={value}
								onChange={onChange}
								onBlur={onBlur}
								rows={3}
								hasError={errors.noteContent?.message}
								isDisabled={isSubmitting || createNoteMutation.isPending}
							/>
							{errors.noteContent && (
								<Text size='footnote' color='error'>
									{errors.noteContent.message}
								</Text>
							)}
						</Stack>
					)}
				/>
				<div style={{ textAlign: 'right' }}>
					<Text size='footnote' color={isOverLimit ? 'error' : 'secondary'}>
						{characterCount} / {MAX_NOTE_LENGTH}
					</Text>
				</div>
			</Stack>

			{noteContent?.trim() && (
				<Stack itemsSpacing='8'>
					<Controller
						name='sendToCommChannels'
						control={control}
						render={({ field: { onChange, value } }) => (
							<Checkbox
								label='Forward note to your Ekho Slack channel'
								isSelected={value}
								onChange={onChange}
								isDisabled={isSubmitting || createNoteMutation.isPending}
							/>
						)}
					/>
					<Controller
						name='sendToBuyer'
						control={control}
						render={({ field: { onChange, value } }) => (
							<Checkbox
								label='Forward note to the buyer via email'
								isSelected={value}
								onChange={onChange}
								isDisabled={isSubmitting || createNoteMutation.isPending}
							/>
						)}
					/>
				</Stack>
			)}

			<Button
				size='large'
				isFullWidth
				onPress={() => {
					submitHandler();
				}}
				isLoading={isSubmitting || createNoteMutation.isPending}>
				Add note
			</Button>
		</Stack>
	);
};
